/**
 * DPL DEW — Scope Group Helpers
 *
 * Previously "roomMaster.ts" — now generic.
 *
 * Key change: ROOM_PRESET_CONFIG and PREBUILT_ITEMS are GONE.
 * Preset loading now queries the live catalogue (catalog_items table)
 * filtered by the scope group's room_type affinity and the section's
 * work-type category. This means:
 *
 *   Scope group (e.g. "Bedroom") + Section work type (e.g. "modular")
 *   → catalogue query: category = "modular" AND room_type = "Bedroom"
 *   → returns whatever the company has configured in their catalogue
 *
 * No hardcoded item IDs. No static preset arrays. The catalogue is the
 * single source of truth for what appears in each scope/section combination.
 */

import type { ScopeGroupType } from './quotation';
import type { ScopeGroupDefinition } from './quotation';
import { settingsService } from '@/services/settingsService';

// ─── Display name generation ──────────────────────────────────────────────────

/**
 * Generate a display name for a new scope group instance.
 * Uses the ScopeGroupDefinition from settings to determine whether
 * to append an instance number.
 *
 * @example
 * // Interior: allowMultiple=true → "Bedroom 2"
 * // Interior: allowMultiple=false → "Kitchen"
 * // IT: allowMultiple=true → "Module 3"
 */
export function generateScopeDisplayName(
  category: ScopeGroupType,
  instanceNumber: number,
  definitions?: ScopeGroupDefinition[],
): string {
  const defs = definitions ?? settingsService.getScopeGroupTypes();
  const def = defs.find(d => d.value === category);
  if (!def) return `${category} ${instanceNumber}`;
  return def.allowMultiple ? `${def.label} ${instanceNumber}` : def.label;
}

// Backward-compat alias used by roomManagementSlice
export function generateRoomDisplayName(
  category: ScopeGroupType,
  instanceNumber: number,
): string {
  return generateScopeDisplayName(category, instanceNumber);
}

// ─── Catalogue-based preset loading ─────────────────────────────────────────

/**
 * Query parameters for loading catalogue presets into a scope group.
 * Both fields are optional — supply what you have for the best match.
 */
export interface ScopePresetQuery {
  /** The scope group type (e.g. "bedroom", "kitchen", "module") */
  scopeCategory: ScopeGroupType;
  /** The section work type (e.g. "modular", "civil", "development") */
  workType?: string;
}

/**
 * Build a Supabase filter query that returns catalogue items
 * appropriate for a given scope + work type combination.
 *
 * MATCHING STRATEGY (in priority order):
 *  1. room_type matches scopeCategory AND category matches workType
 *  2. room_type matches scopeCategory (any category)
 *  3. category matches workType (any room_type)
 *
 * The caller resolves which items to use from the returned lists.
 * Returning all tiers lets the UI offer "strict" vs "relaxed" suggestions.
 */
export interface CataloguePresetResult {
  /** Items matching both scope and work type — show first */
  strict: CataloguePresetItem[];
  /** Items matching scope only — show as secondary suggestions */
  byScope: CataloguePresetItem[];
  /** Items matching work type only — show as tertiary suggestions */
  byWorkType: CataloguePresetItem[];
}

export interface CataloguePresetItem {
  id: string;
  name: string;
  description: string | null;
  specifications: string | null;
  category: string;
  room_type: string | null;
  default_rate: number;
  default_uom: string;
  has_dimensions: boolean;
}

/**
 * Load catalogue presets for a scope group + section work type.
 * Replaces the old ROOM_PRESET_CONFIG + PREBUILT_ITEMS lookup.
 *
 * This is an async function because it queries Supabase directly.
 * The store calls this and inserts the results as line items.
 */
export async function loadCataloguePresets(
  query: ScopePresetQuery,
  supabaseClient: unknown, // typed as unknown to avoid circular dep — pass supabase
): Promise<CataloguePresetResult> {
  const { scopeCategory, workType } = query;

  // Normalise for case-insensitive matching
  const scopeLower = scopeCategory.toLowerCase();
  const wtLower = workType?.toLowerCase();

  const { data: allActive, error } = await supabaseClient
    .from('catalog_items')
    .select('id, name, description, specifications, category, room_type, default_rate, default_uom, has_dimensions')
    .eq('is_active', true)
    .order('category', { ascending: true })
    .order('name', { ascending: true });

  if (error || !allActive) {
    return { strict: [], byScope: [], byWorkType: [] };
  }

  const items = allActive as CataloguePresetItem[];

  const strict: CataloguePresetItem[] = [];
  const byScope: CataloguePresetItem[] = [];
  const byWorkType: CataloguePresetItem[] = [];

  for (const item of items) {
    const itemRoom = item.room_type?.toLowerCase() ?? '';
    const itemCat  = item.category?.toLowerCase() ?? '';

    const matchesScope    = itemRoom === scopeLower || itemRoom === 'general' || itemRoom === '';
    const matchesWorkType = wtLower ? itemCat === wtLower : false;

    if (matchesScope && matchesWorkType) {
      strict.push(item);
    } else if (matchesScope && itemRoom === scopeLower) {
      byScope.push(item);
    } else if (matchesWorkType) {
      byWorkType.push(item);
    }
  }

  return { strict, byScope, byWorkType };
}

// ─── Calculation helpers (unchanged — geometry, not domain) ──────────────────

/**
 * Area = (Length × Breadth) / 929.0304 (cm² → sqft)
 */
export function calculateAreaFromDimensions(length: number, breadth: number): number {
  if (length <= 0 || breadth <= 0) return 0;
  return Number(((length * breadth) / 929.0304).toFixed(2));
}

export function calculateAmountFromArea(area: number, rate: number): number {
  return area * rate;
}

export function calculateItemAmount(
  uom: string,
  quantity: number,
  rate: number,
  sqft?: number,
): number {
  if (uom === 'Sqft' && sqft && sqft > 0) return sqft * rate;
  return quantity * rate;
}

// ─── Legacy stub (kept so old imports compile during migration) ───────────────

/** @deprecated Use loadCataloguePresets() instead */
export function getRoomConfig(category: string, workType?: string): undefined {
  return undefined;
}
